import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

type ClassTaxonomy = { category: string; subcategory: string };

export function useCategoryNames() {
  const [nameMap, setNameMap] = useState<Map<string, string>>(new Map());
  const [classTaxMap, setClassTaxMap] = useState<Map<string, ClassTaxonomy>>(new Map());
  const [taxNameMap, setTaxNameMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = new Map<string, string>();
      const taxByClass = new Map<string, ClassTaxonomy>();
      const taxNames = new Map<string, string>();

      try {
        // Source 1: globalCategories docs (id -> displayName/name)
        // Also carry label taxonomy: category / subcategory are Firestore doc IDs
        // into objectCategoryParents / objectCategories (not human slugs).
        const gc = await getDocs(collection(db, 'globalCategories'));
        gc.forEach((d) => {
          const data = d.data() as any;
          const name = data.displayName || data.name;
          if (name) map.set(d.id, name);
          taxByClass.set(d.id, {
            category: typeof data.category === 'string' ? data.category : '',
            subcategory: typeof data.subcategory === 'string' ? data.subcategory : '',
          });
        });
      } catch (e) {
        console.error('Failed to load globalCategories', e);
      }

      try {
        // Source 2: labelSchemas — class definitions. Each schema doc may carry a classes[] array;
        // read all schema docs and merge every class id -> displayName (+ category/subcategory).
        const ls = await getDocs(collection(db, 'labelSchemas'));
        ls.forEach((d) => {
          const data = d.data() as any;
          const classes = Array.isArray(data.classes) ? data.classes : [];
          classes.forEach((c: any) => {
            const id = c.id || c.classId;
            const name = c.displayName || c.name;
            if (id && name) map.set(id, name);
            if (id) {
              taxByClass.set(id, {
                category: typeof c.category === 'string' ? c.category : '',
                subcategory: typeof c.subcategory === 'string' ? c.subcategory : '',
              });
            }
          });
        });
      } catch (e) {
        console.error('Failed to load labelSchemas', e);
      }

      try {
        // Label-category names: objectCategoryParents/{id}.displayName
        const parents = await getDocs(collection(db, 'objectCategoryParents'));
        parents.forEach((d) => {
          const data = d.data() as any;
          const name = data.displayName || data.name;
          if (name) taxNames.set(d.id, name);
        });
      } catch (e) {
        console.error('Failed to load objectCategoryParents', e);
      }

      try {
        // Label-subcategory names: objectCategories/{id}.displayName
        // (parentCategoryId on these docs points back to objectCategoryParents)
        const subs = await getDocs(collection(db, 'objectCategories'));
        subs.forEach((d) => {
          const data = d.data() as any;
          const name = data.displayName || data.name;
          if (name) taxNames.set(d.id, name);
        });
      } catch (e) {
        console.error('Failed to load objectCategories', e);
      }

      if (!cancelled) {
        // TEMP: verify taxonomy resolution for known released categoryIds
        const sampleIds = [
          'aZIaGJUyag8A2HhgXcuA', // Pen (black ink) — global
          'class_1760689116473', // Uppercase Z — schema
          'class_1762326116373', // 4 — schema
          'FohYPP48mIyuj7lfi2dO', // Plain White Paper — global
          'class_1759867520471', // Lowercase u — schema
        ];
        console.log(
          '[labelTax] sample',
          sampleIds.map((id) => {
            const tax = taxByClass.get(id);
            const catId = tax?.category || '';
            const subId = tax?.subcategory || '';
            return {
              categoryId: id,
              className: map.get(id) || id,
              categoryId_field: catId,
              labelCategory: catId ? (taxNames.get(catId) || catId) : 'Uncategorized',
              subcategoryId_field: subId,
              labelSubcategory: subId ? (taxNames.get(subId) || subId) : 'Uncategorized',
            };
          })
        );

        setNameMap(map);
        setClassTaxMap(taxByClass);
        setTaxNameMap(taxNames);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resolve = (categoryId: string): string => nameMap.get(categoryId) || categoryId;

  const resolveLabelCategory = (categoryId: string): string => {
    const tax = classTaxMap.get(categoryId);
    if (!tax?.category) return 'Uncategorized';
    return taxNameMap.get(tax.category) || tax.category;
  };

  const resolveLabelSubcategory = (categoryId: string): string => {
    const tax = classTaxMap.get(categoryId);
    if (!tax?.subcategory) return 'Uncategorized';
    return taxNameMap.get(tax.subcategory) || tax.subcategory;
  };

  return { nameMap, resolve, resolveLabelCategory, resolveLabelSubcategory, loading };
}
